/**
 * Unified API Service for MDS Queue
 *
 * Consolidates workItemsApi.ts and widget API functionality into a single service
 * with optional authentication support for both main app and widget contexts.
 */

import type {
  WorkItem,
  Nurse,
  Summary,
  AssignRequest,
  AcceptRequest,
  RejectRequest,
  StartAssessmentRequest,
  CompleteRequest,
  ActionResponse,
  StartAssessmentResponse,
  WorkItemsQueryParams,
  SummaryQueryParams,
  AssessmentQueryParams,
  AssessmentsResponse,
  MdsAssessmentsResponse,
  CareAssessmentsResponse,
} from "../types/workItems";

import type {
  ResidentSummaryListResponse,
  GetResidentsResponse,
  RiskFactorSummaryResponse,
  CareQueueNotesResponse,
  ProgressNotesResponse,
  VitalSignsResponse,
  MedicationsResponse,
  InfectionsResponse,
  ResidentOrders,
  FallIncidentsResponse,
  RiskTrendResponse,
  CreateCareQueueNoteRequest,
  CreateCareQueueNoteResponse,
  UnifiedReviewRequest,
  UnifiedReviewResponse,
  HideResidentRequest,
  HideResidentResponse,
  AiFeedbackCreateRequest,
  AiFeedbackCreateResponse,
  AiFeedbackListResponse,
  AiFeedbackContentType,
  FunctionalAbilitiesResponse,
} from "../types/residents";

import { mapRiskDataToSummary } from "../types/residents";

import type {
  CareQueueConfigApiResponse,
  SaveCareQueueConfigApiRequest,
  FacilityRiskConfigApiResponse,
} from "../types/configuration";

import type { FacilityUiDefaults } from "../types/dashboard";

import {
  ApiError,
  FORBIDDEN_MESSAGE,
  UNAUTHORIZED_MESSAGE,
  fetchMarkingRejection,
} from "./apiError";

// Response type for filter options API
export interface FilterOptionsResponse {
  statuses: string[];
  payers: string[];
  assessment_types: string[];
  stations: string[];
  buildings: string[];
  assigned_owners: string[];
}

// CareQueue permission flags returned alongside user info
export interface CareQueuePermissions {
  CanDismissWorkItems: boolean;
  CanScheduleJob: boolean;
  CanConfigureRiskPolicy: boolean;
  CanViewJobStatus: boolean;
  CanViewResidentTab: boolean;
  CanViewMDSTab: boolean;
  CanViewCareAssessmentTab: boolean;
  // NETSOL-11790: when true, the Care Queue is restricted to only the
  // residents assigned to the current user (toggle forced on and locked).
  RestrictToAssignedResidents: boolean;
  // NETSOL-11843: true when the user's facility is licensed for My Assignments
  // (Care Assignments product). Gates the toggle's visibility — the toggle is
  // only shown when licensed.
  MyAssignmentsLicensed: boolean;
}

// Response type for current user info API
export interface UserInfoResponse {
  userSnbr: number;
  userID: string;
  userName: string;
  userTitle: string;
  userInitials: string;
  userStatus: string;
  CareQueuePermissions?: CareQueuePermissions;
  // Token claims
  userId?: string;
  clientId?: string;
  facilityId?: string;
  /** Comma-separated list of all facility IDs the user can access */
  facilityIds?: string;
  admin?: boolean;
}

/** Options of a read that a view may repeat by itself (LIIP-1036). */
export interface ReadOptions {
  /**
   * false: a 5xx is thrown but not reported to onError. A view passes it for an automatic
   * repeat during a transient outage, so the repeats are not reported (isInTransientOutage; contract C12:
   * onError frequency unchanged). Default true.
   */
  reportServerError?: boolean;
}

// API Configuration interface
export interface ApiConfig {
  baseUrl: string;
  auth?: {
    token?: string;
    apiKey?: string;
    ehrToken?: string;
    clientId?: string;
    facilityId?: string;
    customHeaders?: Record<string, string>;
    tokenProvider?: () => Promise<string>;
  };
  onError?: (error: Error) => void;
}

// Helper function to build query string
function buildQueryString(
  params: WorkItemsQueryParams | SummaryQueryParams | AssessmentQueryParams
): string {
  const filteredParams = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value!))}`
    )
    .join("&");
  return filteredParams ? `?${filteredParams}` : "";
}

/**
 * Creates an authenticated fetch function with the provided configuration
 */
function createApiClient(config: ApiConfig) {
  return async function apiFetch(
    endpoint: string,
    options?: RequestInit
  ): Promise<Response> {
    const headers = new Headers(options?.headers);

    // Set default content type for JSON requests with body
    if (!headers.has("Content-Type") && options?.body) {
      headers.set("Content-Type", "application/json");
    }

    // Apply authentication if configured
    if (config.auth?.token) {
      // Bearer Token authentication
      headers.set("Authorization", `Bearer ${config.auth.token}`);
    } else if (config.auth?.tokenProvider) {
      // Dynamic token provider
      try {
        const token = await config.auth.tokenProvider();
        headers.set("Authorization", `Bearer ${token}`);
      } catch (error) {
        console.error("[API Service] Token provider failed:", error);
        throw new Error("Failed to obtain authentication token");
      }
    } else if (config.auth?.apiKey) {
      // API Key authentication
      headers.set("X-API-Key", config.auth.apiKey);
    }

    // Apply custom authentication headers
    if (config.auth?.ehrToken) {
      headers.set("ehrToken", config.auth.ehrToken);
    }
    if (config.auth?.clientId) {
      headers.set("x-client-id", config.auth.clientId);
    }
    if (config.auth?.facilityId) {
      headers.set("x-facility-id", config.auth.facilityId);
    }

    // Apply custom headers
    if (config.auth?.customHeaders) {
      Object.entries(config.auth.customHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });
    }

    // Build full URL
    const url = `${config.baseUrl}${endpoint}`;

    // Make request. A rejection of the request itself (network failure) is marked, so a
    // view repeats it as transient; other errors without a status are not repeated.
    const response = await fetchMarkingRejection(url, {
      ...options,
      headers,
    });

    // Handle authentication errors. The error carries the HTTP status so the host
    // can branch on `error.status === 401`; the message text stays unchanged.
    if (response.status === 401) {
      const error = new ApiError(UNAUTHORIZED_MESSAGE, 401);
      config.onError?.(error);
      throw error;
    }

    if (response.status === 403) {
      const error = new ApiError(FORBIDDEN_MESSAGE, 403);
      config.onError?.(error);
      throw error;
    }

    return response;
  };
}

/**
 * Creates API service with the provided configuration
 */
export function createApiService(config: ApiConfig) {
  const apiFetch = createApiClient(config);

  /**
   * The error for a non-2xx response that apiFetch passed through (401 and 403 never
   * get here). It keeps the HTTP status for every code. Only a 5xx is reported to the
   * host, once, so a host can tell a WorkQueue 503 (transient banner) from an expired
   * session. Another 4xx (400, 404, 409, also on a mutation) is not reported: host
   * callbacks stay as before (AC-001.7); the caller handles the thrown error. The message
   * text is the one these methods have always thrown.
   *
   * `reportServerError: false` leaves a 5xx unreported too: a read that the view repeats
   * by itself during a transient outage reports only its first failure (LIIP-1036;
   * contract C12, onError frequency unchanged).
   */
  const failedResponse = (
    response: Response,
    action: string,
    { reportServerError = true }: ReadOptions = {}
  ): ApiError => {
    const error = new ApiError(`${action}: ${response.statusText}`, response.status);
    if (response.status >= 500 && reportServerError) {
      config.onError?.(error);
    }
    return error;
  };

  /**
   * Headers for the raw-fetch facility lookups (risk-config, ui-defaults). null when the
   * tokenProvider fails: the lookup then falls back to its default, as before.
   */
  const lookupHeaders = async (): Promise<Headers | null> => {
    const headers = new Headers();
    if (config.auth?.token) {
      headers.set("Authorization", `Bearer ${config.auth.token}`);
    } else if (config.auth?.tokenProvider) {
      try {
        const token = await config.auth.tokenProvider();
        headers.set("Authorization", `Bearer ${token}`);
      } catch {
        return null;
      }
    }
    if (config.auth?.clientId) headers.set("x-client-id", config.auth.clientId);
    if (config.auth?.facilityId) headers.set("x-facility-id", config.auth.facilityId);
    return headers;
  };

  /**
   * The body of a raw-fetch facility lookup. A 5xx throws an ApiError with the status (a
   * transient error: the query asks again; no onError call, these lookups never report to
   * the host). Any other non-2xx, or a body that is not JSON, is the fallback (null).
   */
  async function readLookup<T>(response: Response, action: string): Promise<T | null> {
    if (response.status >= 500) {
      throw new ApiError(`${action}: ${response.statusText}`, response.status);
    }
    if (!response.ok) return null;
    try {
      return (await response.json()) as T;
    } catch {
      return null;
    }
  }

  return {
    // GET /api/mds-assessments - Get MDS assessments
    // options.reportServerError: see failedResponse.
    getMdsAssessments: async (
      params?: AssessmentQueryParams,
      signal?: AbortSignal,
      options?: ReadOptions
    ): Promise<MdsAssessmentsResponse> => {
      const queryString = params ? buildQueryString(params) : "";
      const response = await apiFetch(`/api/mds-assessments${queryString}`, {
        signal,
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch MDS assessments", options);
      }

      return response.json();
    },

    // GET /api/care-assessments - Get care assessments
    // options.reportServerError: see failedResponse.
    getCareAssessments: async (
      params?: AssessmentQueryParams,
      signal?: AbortSignal,
      options?: ReadOptions
    ): Promise<CareAssessmentsResponse> => {
      const queryString = params ? buildQueryString(params) : "";
      const response = await apiFetch(`/api/care-assessments${queryString}`, {
        signal,
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch care assessments", options);
      }

      return response.json();
    },

    // GET /api/work-items - Get all work items with optional filters
    getWorkItems: async (
      params?: WorkItemsQueryParams
    ): Promise<AssessmentsResponse> => {
      const queryString = params ? buildQueryString(params) : "";
      const response = await apiFetch(`/api/work-items${queryString}`);

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch work items");
      }

      return response.json();
    },

    // GET /api/work-items/{work_item_id} - Get specific work item
    getSingleWorkItem: async (workItemId: string, signal?: AbortSignal): Promise<WorkItem> => {
      const response = await apiFetch(`/api/work-items/${workItemId}`, {
        signal,
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch work item");
      }

      const data = await response.json();
      // API returns { work_item: {...} }, so extract the work_item
      return data.work_item || data;
    },

    // Alias for getSingleWorkItem to match widget API
    getWorkItem: async (workItemId: string): Promise<WorkItem> => {
      const response = await apiFetch(`/api/work-items/${workItemId}`);

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch work item");
      }

      const data = await response.json();
      // API returns { work_item: {...} }, so extract the work_item
      return data.work_item || data;
    },

    // PATCH /api/work-items/{work_item_id} - Update work item
    updateWorkItem: async (
      workItemId: string,
      updates: Record<string, unknown>
    ): Promise<WorkItem> => {
      const response = await apiFetch(`/api/work-items/${workItemId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to update work item");
      }

      return response.json();
    },

    // GET /api/summary - Get summary statistics
    getSummary: async (params?: SummaryQueryParams): Promise<Summary> => {
      const queryString = params ? buildQueryString(params) : "";
      const response = await apiFetch(`/api/summary${queryString}`);

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch summary");
      }

      return response.json();
    },

    // GET /api/nurses - Get all nurses
    getNurses: async (): Promise<Record<string, Nurse[]>> => {
      const response = await apiFetch(`/api/nurses`);

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch nurses");
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/assign - Assign work item to a nurse
    assignWorkItem: async (
      workItemId: string,
      request: AssignRequest
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to assign work item");
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/accept - Nurse accepts assigned work item
    acceptWorkItem: async (
      workItemId: string,
      request: AcceptRequest
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to accept work item");
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/start-assessment - Start assessment (launches EHR)
    startAssessment: async (
      workItemId: string,
      request: StartAssessmentRequest
    ): Promise<StartAssessmentResponse> => {
      const response = await apiFetch(
        `/api/work-items/${workItemId}/start-assessment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to start assessment");
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/reject - Nurse rejects work item
    rejectWorkItem: async (
      workItemId: string,
      request: RejectRequest
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/reject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to reject work item");
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/complete - Manually complete work item
    completeWorkItem: async (
      workItemId: string,
      request: CompleteRequest
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to complete work item");
      }

      return response.json();
    },

    // GET /api/work-items/{work_item_id}/history - Get work item history (audit trail)
    getWorkItemHistory: async (workItemId: string): Promise<unknown> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/history`);

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch work item history");
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/dismiss - Dismiss work item
    dismissWorkItem: async (
      workItemId: string,
      reason?: string
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/dismiss`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason: reason || null }),
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to dismiss work item");
      }

      return response.json();
    },

    // GET /api/filter-options - Get dynamic filter options.
    // NETSOL-12075: an optional category (MDS/UDA) scopes assessment_types to
    // that category's work items, so each tab's Assessment Type filter lists only
    // its own types (official MDS types on MDS, UDA types on Care Assessments).
    getFilterOptions: async (
      category?: string,
    ): Promise<FilterOptionsResponse> => {
      const qs = category ? `?category=${encodeURIComponent(category)}` : "";
      const response = await apiFetch(`/api/filter-options${qs}`);

      if (!response.ok) {
        // LIIP-1036: the error keeps its status, so a 5xx is transient and the filter options
        // ask again until the API is back. Not reported to onError (it never was: the
        // main loads report the 5xx; contract C12, onError frequency unchanged).
        throw new ApiError(`Failed to fetch filter options: ${response.statusText}`, response.status);
      }

      return response.json();
    },

    // GET /api/auth/me - Get identity from token (no EHR calls, instant)
    // options.reportServerError: see failedResponse.
    getCurrentUser: async (options?: ReadOptions): Promise<UserInfoResponse> => {
      const response = await apiFetch(`/api/auth/me`);

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch current user", options);
      }

      return response.json();
    },

    // GET /api/auth/permissions - Fetch CareQueue permissions from EHR
    // options.reportServerError: see failedResponse.
    getPermissions: async (
      options?: ReadOptions
    ): Promise<{ CareQueuePermissions: CareQueuePermissions }> => {
      const response = await apiFetch(`/api/auth/permissions`);

      if (!response.ok) {
        // NETSOL-11843: throw on non-OK rather than resolving with all-false
        // defaults. A default object is indistinguishable from a real "no
        // permissions" response, so a transient failure (e.g. a 401 during token
        // rotation) used to silently hide the My Assignments toggle and drop the
        // provider security restriction. The Dashboard's catch preserves the
        // last-known-good permissions on failure; only the very first load falls
        // back to the component's built-in defaults.
        throw failedResponse(response, "Failed to fetch permissions", options);
      }

      return response.json();
    },

    // Raw fetch function for advanced use cases
    fetch: apiFetch,

    // ========================================================================
    // Residents API Methods
    // ========================================================================

    // GET /api/residents - Get residents list (maps to ResidentSummaryListResponse)
    // options.reportServerError: see failedResponse.
    getResidentsSummary: async (
      showHidden?: boolean,
      signal?: AbortSignal,
      facilityIds?: string[],
      myAssignments?: boolean,
      options?: ReadOptions
    ): Promise<ResidentSummaryListResponse> => {
      const params = new URLSearchParams();
      if (showHidden) {
        params.set("show_hidden", "true");
      }
      if (facilityIds && facilityIds.length > 0) {
        facilityIds.forEach((id) => params.append("facility_id", id));
      }
      // NETSOL-11791: restrict to residents assigned to the current user.
      if (myAssignments) {
        params.set("my_assignments", "true");
      }
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(`/api/residents${queryString}`, {
        signal,
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch residents summary", options);
      }

      const data: GetResidentsResponse = await response.json();

      return {
        residents: data.residents.map(mapRiskDataToSummary),
        count: data.total,
      };
    },

    // GET /api/residents/{residentId}/riskFactorSummary - Get risk factor summary
    getResidentRiskFactors: async (
      residentId: string,
      asOf?: string,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<RiskFactorSummaryResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/riskFactorSummary${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch risk factors");
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/carequeue_notes - Get care queue notes
    getResidentCareQueueNotes: async (
      residentId: string,
      asOf?: string,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<CareQueueNotesResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/carequeue_notes${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch care queue notes");
      }

      return response.json();
    },

    // POST /api/residents/{residentId}/carequeue_notes - Create care queue note
    createCareQueueNote: async (
      residentId: string,
      request: CreateCareQueueNoteRequest,
      wqId?: string
    ): Promise<CreateCareQueueNoteResponse> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/carequeue_notes${queryString}`,
        {
          method: "POST",
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to create care queue note");
      }

      return response.json();
    },

    // POST /api/residents/{residentId}/review-all - Mark resident as reviewed
    markResidentReviewed: async (
      residentId: string,
      request: UnifiedReviewRequest,
      wqId?: string
    ): Promise<UnifiedReviewResponse> => {
      const params = new URLSearchParams();
      const clientId = config.auth?.clientId;
      const facilityId = config.auth?.facilityId;
      if (clientId) params.set("client_id", clientId);
      if (facilityId) params.set("facility_id", facilityId);
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/review-all${queryString}`,
        {
          method: "POST",
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to mark resident as reviewed");
      }

      return response.json();
    },

    // POST /api/residents/{residentId}/hide - Hide resident for # days
    hideResident: async (
      residentId: string,
      request: HideResidentRequest,
      wqId?: string
    ): Promise<HideResidentResponse> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/hide${queryString}`,
        {
          method: "POST",
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to hide resident");
      }

      return response.json();
    },

    // DELETE /api/residents/{residentId}/hide - Unhide resident
    unhideResident: async (residentId: string, wqId?: string): Promise<void> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/hide${queryString}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to unhide resident");
      }
    },

    // GET /api/residents/{residentId}/progress/notes - Get progress notes
    getResidentProgressNotes: async (
      residentId: string,
      asOf?: string,
      window?: number,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<ProgressNotesResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (window !== undefined) {
        params.set("window", String(window));
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/progress_notes${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch progress notes");
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/metrics/vitalSigns - Get vital signs
    getResidentVitalSigns: async (
      residentId: string,
      asOf?: string,
      window?: number,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<VitalSignsResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (window !== undefined) {
        params.set("window", String(window));
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/metrics/vitalSigns${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch vital signs");
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/medications - Get medications
    getResidentMedications: async (
      residentId: string,
      asOf?: string,
      window?: number,
      newOnly: boolean = true,  // Deprecated parameter
      signal?: AbortSignal,
      wqId?: string
    ): Promise<MedicationsResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (window) {
        params.set("window", window.toString());
      }
      params.set("newOnly", String(newOnly));
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/medications${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch medications");
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/infections - Get infections
    getResidentInfections: async (
      residentId: string,
      asOf?: string,
      window?: number,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<InfectionsResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (window) {
        params.set("window", window.toString());
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/infections${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch infections");
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/orders - Get orders
    getResidentOrders: async (
      residentId: string,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<ResidentOrders> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/orders${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch orders");
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/fall_incidents - Get fall incidents
    getResidentFallIncidents: async (
      residentId: string,
      window?: number,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<FallIncidentsResponse> => {
      const params = new URLSearchParams();
      if (window) {
        params.set("window", window.toString());
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/fall_incidents${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch fall incidents");
      }

      return response.json();
    },

    // GET /api/residents/summary/{patientId}/riskTrend - Get risk trend
    getResidentRiskTrend: async (
      patientId: string,
      signal?: AbortSignal
    ): Promise<RiskTrendResponse> => {
      const response = await apiFetch(
        `/api/residents/summary/${patientId}/riskTrend`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch risk trend");
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/functional_abilities
    getResidentFunctionalAbilities: async (
      residentId: string,
      signal?: AbortSignal,
      wqId?: string,
      days?: number
    ): Promise<FunctionalAbilitiesResponse> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      if (days !== undefined) params.set("days", String(days));
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/functional_abilities${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch functional abilities");
      }

      return response.json();
    },

    // ========================================================================
    // Care Queue Configuration API Methods
    // ========================================================================

    // GET /api/v1/admin/care-queue/config - Get resident tab configuration
    // options.reportServerError: see failedResponse.
    getResidentTabConfiguration: async (
      signal?: AbortSignal,
      options?: ReadOptions
    ): Promise<CareQueueConfigApiResponse> => {
      const response = await apiFetch(`/api/admin/care-queue/config`, {
        signal,
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch configuration", options);
      }

      return response.json();
    },

    // PUT /api/v1/admin/care-queue/config - Save resident tab configuration
    saveResidentTabConfiguration: async (
      configPayload: SaveCareQueueConfigApiRequest
    ): Promise<CareQueueConfigApiResponse> => {
      const response = await apiFetch(`/api/admin/care-queue/config`, {
        method: "PUT",
        body: JSON.stringify(configPayload),
      });

      if (!response.ok) {
        throw failedResponse(response, "Failed to save configuration");
      }

      return response.json();
    },

    // GET /api/facility-settings/risk-config - Get disabled risk factors per facility
    // Uses raw fetch (not apiFetch) so auth errors don't trigger the global onError callback.
    // A 403/401 here is non-fatal — the widget falls back to "all factors enabled".
    // LIIP-1036: a transient failure (5xx, network failure) is NOT turned into that fallback:
    // it throws (ApiError with the status, or the marked fetch rejection), so the query is in
    // a transient error and asks again until the API is back. Still no onError call.
    getRiskFactorConfig: async (
      signal?: AbortSignal
    ): Promise<FacilityRiskConfigApiResponse | null> => {
      const headers = await lookupHeaders();
      if (!headers) return null;

      const response = await fetchMarkingRejection(
        `${config.baseUrl}/api/facility-settings/risk-config`,
        { headers, signal }
      );
      return readLookup<FacilityRiskConfigApiResponse>(response, "Failed to fetch risk config");
    },

    // ========================================================================
    // AI Feedback API Methods
    // ========================================================================

    // POST /api/residents/{residentId}/ai-feedback - Submit AI feedback
    submitAiFeedback: async (
      residentId: string,
      request: AiFeedbackCreateRequest,
      wqId?: string
    ): Promise<AiFeedbackCreateResponse> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/ai-feedback${queryString}`,
        {
          method: "POST",
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to submit AI feedback");
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/ai-feedback - Get existing AI feedback
    getAiFeedback: async (
      residentId: string,
      riskDocumentId: string,
      contentType?: AiFeedbackContentType,
      wqId?: string,
      signal?: AbortSignal
    ): Promise<AiFeedbackListResponse> => {
      const params = new URLSearchParams();
      params.set("risk_document_id", riskDocumentId);
      if (contentType) params.set("content_type", contentType);
      if (wqId) params.set("wq_id", wqId);
      const queryString = `?${params.toString()}`;
      const response = await apiFetch(
        `/api/residents/${residentId}/ai-feedback${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw failedResponse(response, "Failed to fetch AI feedback");
      }

      return response.json();
    },

    // ========================================================================
    // Facility Settings API Methods
    // ========================================================================

    // GET /api/facility-settings/ui-defaults - Get UI defaults for all facilities
    // Returns Record<facilityId, FacilityUiDefaults> keyed by facility_id.
    // Uses raw fetch (not apiFetch) so auth errors don't trigger the global onError callback.
    // A 403/401 here is non-fatal — the widget falls back to Pydantic defaults per facility.
    // LIIP-1036: a transient failure (5xx, network failure) throws instead, so the query asks
    // again until the API is back (see getRiskFactorConfig). Still no onError call.
    getUiDefaults: async (
      signal?: AbortSignal
    ): Promise<Record<string, FacilityUiDefaults> | null> => {
      const headers = await lookupHeaders();
      if (!headers) return null;

      const response = await fetchMarkingRejection(
        `${config.baseUrl}/api/facility-settings/ui-defaults`,
        { headers, signal }
      );
      return readLookup<Record<string, FacilityUiDefaults>>(response, "Failed to fetch UI defaults");
    },

    // GET /api/v1/admin/care-assessment-scheduler/config/{client_id}/{facility_id}
    getSchedulerConfig: async (
      clientId: string,
      facilityId: string,
      signal?: AbortSignal
    ): Promise<{ recurring_workflows: string[] } | null> => {
      if (!clientId || !facilityId) return null;
      // LIIP-1036: every failure of the request itself is passed on, never shown as an empty
      // schedule the user could save over the real one. A network failure is transient (the
      // modal asks again until the API is back). A 401/403 (already reported by apiFetch,
      // no second onError here) or a failed tokenProvider waits for the host's new token,
      // which refetches the schedule.
      const response = await apiFetch(
        `/api/v1/admin/care-assessment-scheduler/config/${clientId}/${facilityId}`,
        { signal }
      );
      // A 5xx is transient: thrown with its status. Not reported to onError (it never was;
      // contract C12, onError frequency unchanged).
      if (response.status >= 500) {
        throw new ApiError(
          `Failed to fetch scheduler configuration: ${response.statusText}`,
          response.status
        );
      }
      if (!response.ok) return null;
      try {
        return await response.json();
      } catch {
        return null;
      }
    },

    // PATCH /api/v1/admin/care-assessment-scheduler/config/{client_id}/{facility_id}
    saveSchedulerConfig: async (
      clientId: string,
      facilityId: string,
      payload: { recurring_workflows: string[] }
    ): Promise<{ recurring_workflows: string[] }> => {
      if (!clientId || !facilityId) {
        throw new Error("Client ID and Facility ID are required");
      }
      const response = await apiFetch(
        `/api/v1/admin/care-assessment-scheduler/config/${clientId}/${facilityId}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw failedResponse(response, "Failed to save scheduler config");
      }
      return response.json();
    },
  };
}

// Type for the API service instance
export type ApiService = ReturnType<typeof createApiService>;

// Default configuration factory for main app usage
export function createDefaultApiService(): ApiService {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  const authToken = import.meta.env.VITE_AUTH_TOKEN;

  if (!baseUrl) {
    throw new Error("VITE_API_BASE_URL environment variable is required");
  }

  const config: ApiConfig = {
    baseUrl,
    // Only include auth token in development mode
    ...(authToken && import.meta.env.DEV && {
      auth: {
        token: authToken,
      },
    }),
  };

  return createApiService(config);
}

// Export a default instance for main app usage
export const apiService = createDefaultApiService();
